
import { Component, Vue, Watch } from 'vue-property-decorator'
import { commonModule, companyModule, installationsModule, offersModule } from '@/store/modules/store-accessor'
import { TimeUtil } from '@/helpers/TimeUtil';
import OfferFilter from '../components/Filter/OfferFilter.vue';
import dayjs from 'dayjs';
import { pagesExportLimit, templatesFilenameLanguage } from '@/helpers/data';
import FileSaver from 'file-saver';
import ExportMenu from "@/components/ExportMenu/ExportMenu.vue";


@Component({
    components: { OfferFilter, ExportMenu }
})
export default class Offers extends Vue {
    tableheaders: any[] = [
        { text: this.$t("offerNumber"), sortable: false, class: 'row-style', value: 'offer_number', width: '110px' },
        { text: this.$t("title"), sortable: false, class: 'row-style', value: 'title' },
        { text: this.$t("installation"), sortable: false, class: 'row-style', value: 'installation_snapshot' },
        { text: this.$t("client"), sortable: false, class: 'row-style', value: 'client_snapshot' },
        { text: this.$t("responsible"), sortable: false, class: 'row-style', value: 'assignee_name' },
        { text: this.$t("validUntil"), sortable: false, class: 'row-style', value: 'valid_until', width: '140px' },
        { text: this.$t("created"), sortable: false, class: 'row-style', value: 'created_at', width: '140px' },
        { text: this.$t("total"), sortable: false, class: 'row-style', value: 'total_cost', align: 'center', width: '110px' },
        { text: this.$t("status"), sortable: false, class: 'row-style', value: 'effective_status', align: 'center', width: '130px' },
    ]

    model: any = {
        search: '',
        selected: [] as string[]
    }

    tableOptions = {
        page: 1,
        itemsPerPage: 20,
    }
    hasMounted = false;
    lastOffersQuery = '';
    skipNextOptionsChange = false;
    timer = 0;
    filterQuery: Record<string, any> = {};

    isDownloadingOffersExport = false;

    get offers() {
        return offersModule.offers;
    }

    get offersPagination() {
        return offersModule.offersPagination;
    }

    get isLoadingTable() {
        return offersModule.isLoadingTable;
    }

    get searchLabel() {
        return this.$t("offersSearch");
    }

    formatDate(date: Date | string): string {
        if (!date) {
            return '-';
        }
        return TimeUtil.formatDate(date as Date)
    }

    async mounted() {
        await Promise.all([
            commonModule.getContactTypeCombo(),
            commonModule.getClientsCombo(),
            commonModule.getPersonnelCombo(),
            commonModule.getPersonnelPartnersCombo(),
        ]);
        commonModule.initSnackbar({});
        this.hasMounted = true;
    }

    destroyed() {
        clearTimeout(this.timer);
        offersModule.clearOffers();
    }


    async refreshData() {
        await this.getOffers(true);
    }

    @Watch('tableOptions', { deep: true })
    async onTableOptionsChange() {
        if (!this.hasMounted) {
            return;
        }

        if (this.skipNextOptionsChange) {
            this.skipNextOptionsChange = false;
            return;
        }

        await this.getOffers();
    }

    onSearchChange() {
        clearTimeout(this.timer);

        this.timer = setTimeout(async () => {
            this.resetToFirstPage();
            await this.getOffers(true);
        }, 500);
    }

    async onFiltersChange(queryObj: Record<string, any>) {
        this.filterQuery = queryObj;
        this.resetToFirstPage();
        await this.getOffers(true);
    }

    resetToFirstPage() {
        if (this.tableOptions.page !== 1) {
            this.skipNextOptionsChange = true;
            this.tableOptions = {
                ...this.tableOptions,
                page: 1,
            };
        }
    }

    async openInstallationSideView(offer: OfferDto) {
        try {
            offersModule.setIsLoadingTable(true);
            await installationsModule.getInstallationInfo(offer.installation_id);
            commonModule.showSideview({
                name: "installations-sideview",
                payload: {
                    installationId: offer.installation_id,
                    tab: 'offers',
                    offerId: offer.id,
                },
            });
        } finally {
            offersModule.setIsLoadingTable(false);
        }
    }

    async getOffers(updQuery = false) {
        const query = this.offersQuery();

        if (!updQuery && query === this.lastOffersQuery) {
            return;
        }

        this.lastOffersQuery = query;
        this.$router.replace({ query: this.routeQueryObject() }).catch(() => { /* */ });
        await offersModule.getAllOffers(query);
    }

    offersQuery(includePagination = true) {
        const params = new URLSearchParams();
        if (includePagination) {
            params.set('page', this.tableOptions.page.toString());
            params.set('limit', this.tableOptions.itemsPerPage.toString());
        }

        if (this.model.search && this.model.search.length > 0) {
            params.set('search', this.model.search);
        }

        Object.keys(this.filterQuery).forEach((key) => {
            const value = this.filterQuery[key];
            if (Array.isArray(value)) {
                value.forEach((item) => params.append(key, item));
            } else if (value !== undefined && value !== null && value !== '') {
                params.set(key, value);
            }
        });

        const query = params.toString();
        return query ? `?${query}` : '';
    }

    routeQueryObject() {
        const queryObj: any = { ...this.filterQuery };

        if (this.model.search && this.model.search.length > 0) {
            queryObj.search = this.model.search;
        }

        return queryObj;
    }

    formatCurrency(amount: number) {
        return Number(amount || 0).toFixed(2);
    }

    statusText(status: string) {
        return this.$i18n.t(status);
    }

    statusColor(status: string) {
        switch (status) {
            case 'ACCEPTED':
                return 'darkGreen';
            case 'REJECTED':
            case 'EXPIRED':
                return 'error';
            case 'SENT':
                return 'primary';
            case 'DRAFT':
                return 'grayBold';
            default:
                return 'textColor2';
        }
    }

    clientName(client: ClientSnapshotDto) {
        if (!client) return '';
        const personName = `${client.first_name || ''} ${client.last_name || ''}`.trim();
        if (client.is_business) {
            const entity = client.entity_name || '';
            return entity ? (personName ? `${entity} (${personName})` : entity) : personName;
        }
        return personName;
    }

    installationText(installation: InstallationSnapshotDto) {
        if (!installation) {
            return '';
        }

        return [
            installation?.tag_id?.split('_')?.pop(),
            installation.address,
            installation.city,
            installation.area,
            installation.zip_code
        ].filter(Boolean).join(' | ');
    }

    downloadExportLengthCheck(type: string) {
        if (type === 'pdf') {
            return this.offersPagination.total > 0 && this.offersPagination.total <= pagesExportLimit();
        }
        return this.offersPagination.total > 0;
    }

    async donwloadOffersExport(format: string) {
        this.isDownloadingOffersExport = true;

        try {
            const data = {
                user_timezone: dayjs.tz.guess(),
                language: localStorage.getItem("lang"),
                format: format,
            };

            const filetype = format === 'pdf' ? 'pdf' : 'xlsx'

            const company = await companyModule.getCompanyInfo();
            const filename = `${templatesFilenameLanguage()}_${company.entity_name}_DfB_offers_${dayjs().format("YYYY_MM_DD_HH_mm_ss")}.${filetype}`.replace(/ /g, "_");
            const pdf = await offersModule.getOffersPdfExportAction({
                query: this.offersQuery(false),
                data: data,
            });
            const file = new File([pdf], filename);
            FileSaver.saveAs(file);
        } finally {
            this.isDownloadingOffersExport = false;
        }
    }

}
